import { ApiError } from './api-error';

/**
 * Errores de formulario normalizados.
 *
 * El backend valida con Zod y devuelve el detalle del fallo en `ApiError.details` con esta forma
 * (ver `backend/src/utils/zod-error.ts` → `validationDetailsFrom`):
 *
 *   {
 *     "fieldErrors": { "email": ["Email inválido"], "reglasGenerales.1": ["Demasiado grande…"] },
 *     "formErrors":  ["Debes enviar al menos un campo para actualizar"],
 *     "issues": [{ "location": "body", "path": "email", "code": "invalid_format", "message": "…" }]
 *   }
 *
 * `issues` es la fuente de verdad (trae `location` + `path`, así que nunca hay duda de a qué campo
 * pertenece un mensaje). Cuando está presente, este mapeo reparte cada issue entre:
 *
 *  - el campo del formulario que lo produjo, probando la ruta exacta, sus prefijos
 *    (`reglasGenerales.1` → `reglasGenerales`) y el último segmento (`body.email` → `email`);
 *  - el aviso general del formulario cuando no se puede atribuir a un campo: los mensajes de
 *    `formErrors` (un `refine` sobre el objeto completo, con ruta vacía) y cualquier issue que no
 *    venga del cuerpo (`params`, `query`).
 *
 * Si `details` no trae `issues` (una `ApiError` con detalle libre o la forma antigua de
 * `ZodError.flatten()`, donde TODO venía bajo la clave `body`), se recorre el JSON con el mismo
 * reparto tolerante de siempre.
 *
 * Las validaciones por campo evidentes (email mal formado, contraseña corta…) las hace la propia
 * pantalla antes de enviar, espejo de los esquemas del backend (`auth.schemas.ts`), para no
 * depender del viaje al servidor.
 */

export interface FormErrors {
  /** Mensajes que no se pudieron asociar a un campo concreto. */
  formMessages: string[];
  /** Mensajes por nombre de campo (`email`, `password`…). */
  fieldErrors: Record<string, string[]>;
}

export const EMPTY_FORM_ERRORS: FormErrors = { formMessages: [], fieldErrors: {} };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Campo al que pertenece una ruta de Zod. Se prueban, en orden: la ruta completa, sus prefijos
 * (de más largo a más corto) y el último segmento, y gana la primera que exista en el formulario.
 *
 * Así valen las tres formas que puede traer el backend:
 *   `email`              → `email`
 *   `body.email`         → `email`   (forma antigua de `flatten()`)
 *   `reglasGenerales.1`  → `reglasGenerales`   y   `datos.cbu` → `datos`
 */
function fieldFromKey(key: string, fields: readonly string[]): string | null {
  const segments = key.split('.');
  const candidates = segments.map((_segment, index) => segments.slice(0, index + 1).join('.'));
  const last = segments[segments.length - 1];
  if (last && !candidates.includes(last)) candidates.push(last);

  return candidates.find((candidate) => fields.includes(candidate)) ?? null;
}

function pushUnique(list: string[], message: string): void {
  if (!list.includes(message)) list.push(message);
}

function addFieldMessage(output: FormErrors, field: string, message: string): void {
  const current = output.fieldErrors[field] ?? [];
  pushUnique(current, message);
  output.fieldErrors[field] = current;
}

/** Reparte un mensaje: al campo del formulario si se puede atribuir, y si no al aviso general. */
function addMessage(
  output: FormErrors,
  fields: readonly string[],
  key: string,
  message: string,
): void {
  const trimmed = message.trim();
  if (!trimmed) return;

  const field = fieldFromKey(key, fields);
  if (field) {
    addFieldMessage(output, field, trimmed);
    return;
  }

  pushUnique(output.formMessages, trimmed);
}

/** Recorre el detalle de Zod (claves, arrays u objetos anidados) acumulando mensajes. */
function collect(value: unknown, fields: readonly string[], key: string, output: FormErrors): void {
  if (typeof value === 'string') {
    addMessage(output, fields, key, value);
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) collect(item, fields, key, output);
    return;
  }

  if (isRecord(value)) {
    for (const [childKey, childValue] of Object.entries(value)) {
      // `formErrors` / `fieldErrors` son envoltorios de Zod: no forman parte de la ruta.
      const isWrapper = childKey === 'formErrors' || childKey === 'fieldErrors';
      collect(childValue, fields, isWrapper ? key : key ? `${key}.${childKey}` : childKey, output);
    }
  }
}

/**
 * Lee `details.issues` (la forma canónica del backend). Devuelve `true` si el detalle traía
 * issues, es decir, si ya se encargó de repartir todo y no hay que mirar el JSON genérico.
 */
function collectIssues(
  details: unknown,
  fields: readonly string[],
  output: FormErrors,
): boolean {
  if (!isRecord(details) || !Array.isArray(details.issues) || details.issues.length === 0) {
    return false;
  }

  let read = 0;

  for (const raw of details.issues) {
    if (!isRecord(raw)) continue;
    const message = typeof raw.message === 'string' ? raw.message.trim() : '';
    if (!message) continue;
    read += 1;

    const location = typeof raw.location === 'string' ? raw.location : 'body';
    const path = typeof raw.path === 'string' ? raw.path : '';

    // `path` vacío = fallo del objeto completo (un `refine`); `location` distinta de `body` =
    // problema de la petición (params/query), no de un campo del formulario.
    if (location !== 'body' || !path) {
      pushUnique(output.formMessages, message);
      continue;
    }

    addMessage(output, fields, path, message);
  }

  return read > 0;
}

/**
 * Convierte el error de una mutación en errores de formulario.
 *
 * `fields` son los nombres de campo de la pantalla (`['email', 'password']`), los únicos a los
 * que se puede atribuir un mensaje del backend (así no se pinta un error bajo un campo que no
 * existe en el formulario).
 */
export function formErrorsFromApiError(
  error: unknown,
  fields: readonly string[] = [],
): FormErrors {
  const result: FormErrors = { formMessages: [], fieldErrors: {} };

  if (!(error instanceof ApiError) || error.details === undefined) return result;

  // El detalle del backend (`{ fieldErrors, formErrors, issues }`) trae `issues` con `location` y
  // `path`: eso es lo que se usa. Si no viene (detalle libre, o la forma antigua del `flatten()`),
  // se recorre el JSON con el reparto tolerante.
  if (!collectIssues(error.details, fields, result)) {
    collect(error.details, fields, '', result);
  }

  return result;
}

/** Primer mensaje de un campo (el que se pinta debajo del `Input`). */
export function fieldError(errors: FormErrors, field: string): string | undefined {
  return errors.fieldErrors[field]?.[0];
}

/** ¿Hay algo que mostrar? */
export function hasFormErrors(errors: FormErrors): boolean {
  return errors.formMessages.length > 0 || Object.keys(errors.fieldErrors).length > 0;
}

/**
 * Construye `FormErrors` a partir de la validación del cliente (una entrada por campo, con
 * `undefined` cuando el campo está bien).
 *
 * Es el puente entre las funciones de validación de cada pantalla
 * (`src/features/<modulo>/…-validation.ts`, que devuelven `string | undefined` por campo) y la
 * forma que consumen `Field`, `fieldError` y `FormErrorAlert`.
 *
 *   formErrorsFromFields({ email: validateEmail(email), nombre: validateNombre(nombre) })
 */
export function formErrorsFromFields(
  fields: Record<string, string | undefined>,
): FormErrors {
  const fieldErrors: Record<string, string[]> = {};

  for (const [field, message] of Object.entries(fields)) {
    const trimmed = message?.trim();
    if (trimmed) fieldErrors[field] = [trimmed];
  }

  return { formMessages: [], fieldErrors };
}
